import { useState } from 'react';

import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import PropTypes from 'prop-types';
import { generatePath, useNavigate, useLocation } from 'react-router-dom';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Chip from '@zenbusiness/zui/Chip';
import IconButton from '@zenbusiness/zui/IconButton';
import Menu from '@zenbusiness/zui/Menu';
import MenuItem from '@zenbusiness/zui/MenuItem';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';

import {
  ENABLE_DASHBOARD_SUBSCRIPTION_DNR,
  ENABLE_M2A,
  ENABLE_UJM_RA_STANDALONE_DNR,
  ENABLE_UJM_DNR_ZB_MONEY_PRO,
  ENABLE_UJM_DNR_PRO_PLAN,
  ENABLE_UJM_DNR_WFC,
  ENABLE_UJM_DNR_PREM_PLAN,
  PHASE_THREE_SUBSCRIPTION_MANAGEMENT
} from '../../../constants/experimentsFlags';
import { faqs } from '../constants';
import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';
import getServiceVersionless from '../utils/getServiceVersionless';
import {
  getSelfServiceDnrState,
  TRIAL_DNR_ELIGIBLE_SERVICES
} from '../utils/selfServiceDnrEligibility';

export function ThreeDotMenu({
  subscription,
  paymentStatus,
  canChangePeriod,
  billingChangeSavings,
  domesticJurisdiction,
  isIncluded = false,
  businessEntityUuid
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { formatMessage } = useIntl();

  const phaseThreeEnabled = useExperimentFlag(PHASE_THREE_SUBSCRIPTION_MANAGEMENT, false);
  const m2aEnabled = useExperimentFlag(ENABLE_M2A, false);
  const subscriptionDnrEnabled = useExperimentFlag(ENABLE_DASHBOARD_SUBSCRIPTION_DNR, false);
  const enableUjmRaStandaloneDnr = useExperimentFlag(ENABLE_UJM_RA_STANDALONE_DNR, false);
  const enableUjmDnrZbMoneyPro = useExperimentFlag(ENABLE_UJM_DNR_ZB_MONEY_PRO, false);
  const enableUjmDnrProPlan = useExperimentFlag(ENABLE_UJM_DNR_PRO_PLAN, false);
  const enableUjmDnrWfc = useExperimentFlag(ENABLE_UJM_DNR_WFC, false);
  const enableUjmDnrPremPlan = useExperimentFlag(ENABLE_UJM_DNR_PREM_PLAN, false);

  const {
    subscriptionStatus,
    isFormationComplete,
    isPostPurchaseComplete,
    service,
    orderBillingItemUuid
  } = subscription;

  const serviceVersionless = getServiceVersionless(service);

  const { modalFlow } = changeFlow;
  const { restartSubscription, changeBillingIntervalAnnual } = modalFlow;

  const canRestart = subscriptionStatus === 'cancel_renewal'
    && paymentStatus !== 'failed'
    && service !== 'ongoing_bof_compliance';
  const canUseLegacyDnrSupportFallback = !['cancel_renewal'].includes(subscriptionStatus)
    && (paymentStatus && paymentStatus !== 'failed')
    && isFormationComplete;
  // Legacy restart/change-billing modals can still open an inline support
  // fallback. Keep that behavior separate from the menu's Contact Support row.
  const requiresSupportModalFlow = paymentStatus === 'failed'
    || ['deferred', 'created', 'unpaid'].includes(subscriptionStatus);
  const shouldNotSeeMenu = ['cancel_renewal_pending'].includes(subscriptionStatus);
  const canChangeBillingInterval = m2aEnabled && canChangePeriod
    && paymentStatus !== 'failed' && isPostPurchaseComplete
    && !['cancel_renewal_pending', 'cancel_renewal', 'active_pending'].includes(subscriptionStatus);

  const hasFAQ = faqs[serviceVersionless];

  const jurisdictionLimitsDefault = ['CA', 'CO', 'DE', 'NY', 'IL', 'HI', 'TN', 'DC'];

  const serviceConfigs = {
    registered_agent_service: { jurisdictionLimits: null, ujmHandled: enableUjmRaStandaloneDnr },
    zb_money_pro: {
      jurisdictionLimits: jurisdictionLimitsDefault, ujmHandled: enableUjmDnrZbMoneyPro
    },
    pro_plan: { jurisdictionLimits: null, ujmHandled: enableUjmDnrProPlan },
    worry_free_service: { jurisdictionLimits: null, ujmHandled: enableUjmDnrWfc },
    premium_plan: { jurisdictionLimits: null, ujmHandled: enableUjmDnrPremPlan }
  };

  const isDnrAllowedForJurisdiction = (jurisdiction) => {
    if (serviceConfigs?.[serviceVersionless]?.jurisdictionLimits === null) return true;
    const limits = serviceConfigs?.[serviceVersionless]?.jurisdictionLimits
      ?? jurisdictionLimitsDefault;
    return limits.includes(jurisdiction);
  };

  const dnrState = getSelfServiceDnrState({
    subscription,
    paymentStatus,
    isIncluded,
    isSubscriptionDnrEnabled: subscriptionDnrEnabled
  });

  const showCancelTrialItem = dnrState.canCancelTrial;
  const showCancelSubscriptionItem = dnrState.canCancelSubscription;
  const showLegacySupportFallback = canUseLegacyDnrSupportFallback
    && isDnrAllowedForJurisdiction(domesticJurisdiction)
    && requiresSupportModalFlow
    && !isIncluded
    && !showCancelTrialItem;

  const handleOpenRestartModalFlow = () => {
    setChangeFlow((prevState) => ({
      ...prevState,
      modalFlow: {
        ...modalFlow,
        ...(requiresSupportModalFlow ? {
          error: true,
          restartSubscription: { ...restartSubscription, start: true }
        } : {
          restartSubscription: { ...restartSubscription, start: true, stepOne: true }
        })
      }
    }));
  };

  const handleOpenM2AModalFlow = () => setChangeFlow((prevState) => ({
    ...prevState,
    modalFlow: {
      ...modalFlow,
      ...(requiresSupportModalFlow ? {
        error: true,
        changeBillingIntervalAnnual: { ...changeBillingIntervalAnnual, start: true }
      } : {
        changeBillingIntervalAnnual: { ...changeBillingIntervalAnnual, start: true, stepOne: true }
      })
    }
  }));

  const open = Boolean(anchorEl);

  const handleOpenDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (action) => () => {
    if (action) action();
    setAnchorEl(null);
  };

  const trackingData = {
    elementName: 'ThreeDotMenu',
    elementValue: service,
    appSection: 'business',
    appPageName: 'active_subscription_list',
    canonicalPath: pathname
  };

  const trackContactCS = useButtonPressedCallback({ ...trackingData, elementText: 'Contact Support' });
  const trackCancelTrial = useButtonPressedCallback({ ...trackingData, elementText: 'Cancel Trial' });
  const trackCancelSubscription = useButtonPressedCallback({ ...trackingData, elementText: 'Cancel Subscription' });
  const trackRestartSubscription = useButtonPressedCallback({ ...trackingData, elementText: 'Restart Subscription' });
  const trackChangeBillingInterval = useButtonPressedCallback({ ...trackingData, elementText: 'Change billing' });
  const trackFAQ = useButtonPressedCallback({ ...trackingData, elementText: 'See FAQs' });

  // The Contact Support entries are mutually exclusive: the default row shows
  // when no support-modal flow is needed, and the legacy row covers support
  // fallback cases. Keep their show conditions disjoint.
  const config = [
    {
      key: 'contact-support',
      action: () => { trackContactCS(); navigate(ROUTES.help); },
      show: !requiresSupportModalFlow && !isIncluded,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.contactSupport' })
    },
    {
      key: 'contact-support-cancel',
      action: () => { trackContactCS(); navigate(ROUTES.help); },
      show: showLegacySupportFallback,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.contactSupport' })
    },
    {
      key: 'cancel-trial',
      action: () => {
        trackCancelTrial();
        navigate(generatePath(
          ROUTES.entitySubscriptionCancel,
          { businessEntityUuid, orderBillingItemUuid }
        ));
      },
      show: showCancelTrialItem,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.cancelTrial' })
    },
    {
      key: 'cancel-subscription',
      action: () => {
        trackCancelSubscription();
        navigate(generatePath(
          ROUTES.entitySubscriptionCancel,
          { businessEntityUuid, orderBillingItemUuid }
        ));
      },
      show: showCancelSubscriptionItem,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.cancelSubscription' })
    },
    {
      key: 'restart-subscription',
      action: () => {
        trackRestartSubscription();
        if (TRIAL_DNR_ELIGIBLE_SERVICES.includes(serviceVersionless)) {
          navigate(generatePath(
            ROUTES.entitySubscriptionRestart,
            { businessEntityUuid, orderBillingItemUuid }
          ));
        } else {
          handleOpenRestartModalFlow();
        }
      },
      show: canRestart && phaseThreeEnabled,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.restartSubscription' })
    },
    {
      key: 'change-billing',
      action: () => { trackChangeBillingInterval(); handleOpenM2AModalFlow(); },
      show: canChangeBillingInterval,
      label: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Text size="sm">
            {formatMessage({ id: 'dashboard.subscriptions.menu.changeBilling' })}
          </Text>
          <Chip label={formatMessage({ id: 'dashboard.subscriptions.menu.savePct' }, { pct: billingChangeSavings })} size="small" color="success" />
        </Stack>
      )
    },
    {
      key: 'see-faqs',
      action: () => { trackFAQ(); window.location.href = hasFAQ; },
      show: Boolean(hasFAQ),
      label: formatMessage({ id: 'dashboard.subscriptions.menu.seeFaqs' })
    }
  ];

  const hasMenuItems = config.some(({ show }) => show);

  return (
    <>
      <IconButton
        aria-label="options"
        aria-controls="options-dropdown"
        aria-haspopup="true"
        icon={<MoreHorizRoundedIcon />}
        label={formatMessage({ id: 'dashboard.subscriptions.menu.moreOptions' })}
        clickstreamProps={{ elementName: 'subscription_more_options' }}
        onClick={handleOpenDropdown}
        disabled={!hasMenuItems}
        size="large"
        sx={{ ...(shouldNotSeeMenu && { visibility: 'hidden' }) }}
      />
      {hasMenuItems && (
        <Menu
          id="options-dropdown"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableRestoreFocus
        >
          {config.map(({
            key, label, show, action
          }) => (
            show && (
              <MenuItem key={key} onClick={handleClose(action)}>
                {typeof label === 'string' ? <Text size="sm">{label}</Text> : label}
              </MenuItem>
            )
          ))}
        </Menu>
      )}
    </>
  );
}

ThreeDotMenu.propTypes = {
  subscription: PropTypes.shape({
    subscriptionStatus: PropTypes.string,
    isFormationComplete: PropTypes.bool,
    isPostPurchaseComplete: PropTypes.bool,
    service: PropTypes.string,
    period: PropTypes.string,
    contractInterval: PropTypes.string,
    inTrial: PropTypes.bool,
    payment: PropTypes.arrayOf(PropTypes.shape({
      paymentStatus: PropTypes.string
    })),
    orderBillingItemUuid: PropTypes.string
  }).isRequired,
  paymentStatus: PropTypes.string.isRequired,
  canChangePeriod: PropTypes.bool.isRequired,
  billingChangeSavings: PropTypes.number,
  domesticJurisdiction: PropTypes.string,
  isIncluded: PropTypes.bool,
  businessEntityUuid: PropTypes.string
};
